import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones, ShieldCheck, Clock3, MapPin } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { UKClock } from "../components/site/UKClock";
import { Stats } from "../components/site/Stats";

const UKMap = lazy(() => import("../components/site/UKMap").then((m) => ({ default: m.UKMap })));
const ScrollStory = lazy(() => import("../components/site/ScrollStory").then((m) => ({ default: m.ScrollStory })));
import { Faq } from "../components/site/Faq";
import { PostcodeFinder } from "../components/site/PostcodeFinder";
import { Icon } from "../components/site/Icon";
import { HotlineBtn, ReportBtn, Eyebrow, Reveal, SectionHead, GlowCard, CtaBand, ArrowLink } from "../components/site/Kit";
import { TRADES, PILLARS, AUDIENCES, FAQS, HEROES } from "../content/site";
import { FM_PAGES } from "../content/fm";

const Hero = () => (
  <section data-testid="home-hero" className="relative isolate overflow-hidden border-b hairline">
    <div className="absolute inset-0 -z-10" aria-hidden="true">
      <img src="/images/hero-night.webp" srcSet="/images/hero-night-sm.webp 960w, /images/hero-night.webp 1600w" sizes="100vw" alt="" className="h-full w-full object-cover object-[70%_50%] opacity-70" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/50" />
      <div className="gold-glow absolute -left-48 top-10 h-[640px] w-[640px]" />
    </div>
    <div className="container-x flex min-h-[calc(100svh-108px)] flex-col justify-center py-16 sm:py-20">
      <UKClock />
      <h1 className="mt-8 max-w-4xl text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[5.6rem] font-semibold tracking-[-0.045em]">
        <span className="rise block" style={{ "--d": "120ms" }}>When it breaks,</span>
        <span className="rise block text-gold-grad" style={{ "--d": "240ms" }}>we answer.</span>
      </h1>
      <p className="rise mt-8 max-w-xl text-base md:text-lg leading-relaxed text-paper/80" style={{ "--d": "360ms" }}>
        {HEROES["/"].intro}
      </p>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
        <HotlineBtn testid="hero-hotline-link" />
        <ReportBtn testid="hero-report-button" />
        <Link to="/request-callout" data-testid="hero-request-callout-link" className="group mt-2 inline-flex items-center gap-2 px-2 text-[15px] text-mist hover:text-gold sm:mt-0">
          Not an emergency? Request a call-out
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      </div>
      <div className="mt-14 hidden items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-mist md:flex">
        <span className="flex items-center gap-2"><Headphones className="h-4 w-4 text-gold" aria-hidden="true" /> Real people, 24/7</span>
        <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" aria-hidden="true" /> Vetted local trades</span>
        <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" aria-hidden="true" /> UK-wide coverage</span>
      </div>
    </div>
    <Marquee />
  </section>
);

const Marquee = () => {
  const row = [...TRADES, ...TRADES];
  return (
    <div className="overflow-hidden border-t hairline bg-navy/70 py-4 backdrop-blur" aria-label="Trades we cover">
      <div className="marquee-track flex w-max gap-10 whitespace-nowrap">
        {row.map((t, i) => (
          <span key={i} aria-hidden={i >= TRADES.length} className="flex items-center gap-10 font-display text-lg text-paper/60">
            {t}<span className="h-1.5 w-1.5 rounded-full bg-gold/60" />
          </span>
        ))}
      </div>
    </div>
  );
};

const Manifesto = () => (
  <section className="container-x py-24 sm:py-36" data-testid="home-manifesto">
    <div className="grid gap-12 lg:grid-cols-[0.8fr_2fr]">
      <Reveal><Eyebrow>Welcome to NECOS</Eyebrow></Reveal>
      <Reveal delay={100}>
        <p className="font-display text-2xl sm:text-4xl lg:text-[2.9rem] font-medium leading-[1.18] tracking-[-0.025em] text-paper/90">
          One number for every property in your portfolio. <span className="text-gold">Answered by real people at any hour</span>, triaged with care, and resolved by trusted local trades — <span className="text-mist">from Land&rsquo;s End to John o&rsquo; Groats.</span>
        </p>
        <div className="mt-10 flex flex-wrap gap-6">
          <ArrowLink to="/about" testid="home-about-link">More about NECOS</ArrowLink>
          <ArrowLink to="/about/process" testid="home-process-link">How our process works</ArrowLink>
        </div>
      </Reveal>
    </div>
  </section>
);

const Coverage = () => (
  <section className="relative overflow-hidden py-24 sm:py-32" data-testid="home-coverage">
    <div className="container-x grid items-center gap-16 lg:grid-cols-2">
      <div>
        <SectionHead
          eyebrow="Nationwide coverage, local expertise"
          title="Everywhere you own. Someone local who knows it."
          intro="We cover the whole of the UK through a network of vetted local contractors. Each job goes to professionals who know their community — the personal, responsive service national chains can't match, with the reliability of a national operation."
        />
        <Reveal delay={150} className="mt-10 grid max-w-lg grid-cols-2 gap-4">
          {[["Cities & towns", "Urban centres to rural properties"], ["One desk", "A single point of contact, 24/7"]].map(([t, c]) => (
            <div key={t} className="rounded-2xl border hairline bg-deep/60 p-5">
              <p className="font-display text-lg text-paper">{t}</p>
              <p className="mt-1 text-sm text-mist">{c}</p>
            </div>
          ))}
        </Reveal>
        <Reveal delay={200} className="mt-10 max-w-lg"><PostcodeFinder variant="compact" /></Reveal>
      </div>
      <Reveal delay={100}><Suspense fallback={<div className="aspect-[3/4] w-full" />}><UKMap /></Suspense></Reveal>
    </div>
  </section>
);

const Triage = () => (
  <section className="border-y hairline bg-deep/40" data-testid="home-triage">
    <div className="container-x grid gap-14 py-24 sm:py-32 lg:grid-cols-2 lg:items-center">
      <Reveal className="relative overflow-hidden rounded-3xl border hairline">
        <img src="/images/story-triage-sm.webp" alt="Call handlers with headsets working a night shift" loading="lazy" className="aspect-[4/3] w-full object-cover" />
        <div className="absolute bottom-4 left-4 rounded-full bg-navy/80 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold backdrop-blur">Telephone triage · 24/7</div>
      </Reveal>
      <div>
        <SectionHead
          eyebrow="Our triage service"
          title="Real people. Calm guidance. Fewer wasted call-outs."
          intro="Your tenants speak to trained, empathetic call handlers who assess urgency and guide them through safe self-help steps. Many issues are resolved over the phone — so contractors are only sent when they're genuinely needed, saving you time and money."
        />
        <Reveal delay={150} className="mt-10 flex flex-wrap items-center gap-6">
          <ArrowLink to="/emergency-call-out/triage" testid="home-triage-link">Explore triage</ArrowLink>
          <ArrowLink to="/emergency-call-out" testid="home-emergency-link">Emergency call-out</ArrowLink>
        </Reveal>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="container-x py-24 sm:py-32" data-testid="home-services">
    <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
      <SectionHead eyebrow="Facilities management" title="Beyond the emergency. One partner, one platform." />
      <Reveal><ArrowLink to="/facilities-management" testid="home-fm-link">All facilities services</ArrowLink></Reveal>
    </div>
    <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {FM_PAGES.map((s, i) => (
        <GlowCard key={s.slug} as={Link} to={`/facilities-management/${s.slug}`} delay={(i % 5) * 60} className="group flex min-h-[190px] flex-col justify-between p-6" data-testid={`home-fm-card-${s.slug}`}>
          <Icon name={s.icon} className="h-7 w-7 text-gold" />
          <div>
            <p className="font-mono text-[11px] text-mist">{String(i + 1).padStart(2, "0")}</p>
            <h3 className="mt-2 text-lg font-semibold leading-snug tracking-tight group-hover:text-gold transition-colors">{s.title}</h3>
          </div>
        </GlowCard>
      ))}
    </div>
  </section>
);

const Audiences = () => (
  <section className="relative overflow-hidden border-t hairline py-24 sm:py-32" data-testid="home-audiences">
    <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.4fr]">
      <div className="lg:sticky lg:top-40 lg:self-start">
        <SectionHead eyebrow="Who we work with" title="Built for portfolios that never sleep." intro="Tailored plans — pay per call-out or per unit — priced on application." />
        <Reveal delay={120} className="mt-8"><ArrowLink to="/about/packages" testid="home-packages-link">See packages</ArrowLink></Reveal>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {AUDIENCES.map((a, i) => (
          <GlowCard key={a.title} delay={i * 70} className="p-8">
            <Clock3 className="h-5 w-5 text-gold" aria-hidden="true" />
            <h3 className="mt-8 text-2xl font-semibold tracking-tight">{a.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-mist">{a.copy}</p>
          </GlowCard>
        ))}
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="border-t hairline bg-[#041626] py-24 sm:py-32" data-testid="home-pillars">
    <div className="container-x">
      <SectionHead eyebrow="Why choose NECOS" title="The number one call for emergency repairs." />
      <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border hairline bg-gold/10 sm:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 80} className="bg-[#041626] p-8 sm:p-10">
            <p className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</p>
            <h3 className="mt-10 text-xl font-semibold tracking-tight">{p.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-mist">{p.copy}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FaqTeaser = () => (
  <section className="container-x grid gap-12 py-24 sm:py-32 lg:grid-cols-[1fr_1.6fr]" data-testid="home-faq">
    <div>
      <SectionHead eyebrow="Questions" title="Straight answers." intro="Landlord, property manager, tenant or contractor — here's what people ask us most." />
      <Reveal delay={120} className="mt-8"><ArrowLink to="/about/faq" testid="home-faq-link">View all questions</ArrowLink></Reveal>
    </div>
    <Reveal delay={100}><Faq items={FAQS.slice(0, 4)} testid="home-faq-list" /></Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo />
      <Hero />
      <Manifesto />
      <Suspense fallback={<div className="min-h-[100vh]" />}><ScrollStory /></Suspense>
      <Coverage />
      <Triage />
      <Stats />
      <Services />
      <Audiences />
      <Pillars />
      <FaqTeaser />
      <CtaBand />
    </>
  );
}
